import { parse } from 'papaparse';

import {
  downloadImportDraftCsv,
  getImportJobStatus,
} from '~entities/import-job/api/import-job-api';
import type {
  ImportJobResponse,
  ImportJobStagedImage,
  SourcePlatform,
} from '~entities/import-job/api/types';
import {
  applyPlatformClassRowTransforms,
  applyPlatformFamilyRowTransforms,
} from '~features/import-wizard/platform-row-transforms';
import type { ClassWizardResult } from '~pages/import-wizard/step-1-classes';
import type { ClassPhotoMapping } from '~pages/import-wizard/step-2-photos';
import type { FamilyWizardResult } from '~pages/import-wizard/step-3-families';
import type { ClassData } from '~pages/classes/bulk-upload/validation';
import type { ParentRegistrationData } from '~pages/parents/bulk-upload/validation';
import { countParentGroupsFromCsvRows } from '~pages/parents/bulk-upload/count-parent-groups';

function csvToFile(content: string, filename: string): File {
  return new File([content], filename, { type: 'text/csv' });
}

export async function rehydrateClassResult(
  jobId: string,
  platform: SourcePlatform,
  filename?: string | null,
): Promise<ClassWizardResult | null> {
  const content = await downloadImportDraftCsv(jobId, 'classes');
  const rows = parse<ClassData>(content, {
    header: true,
    skipEmptyLines: true,
  }).data;
  const transformedRows = applyPlatformClassRowTransforms(rows, platform);
  const titles = transformedRows
    .map((row, rowIndex) => ({
      rowIndex,
      title: String(row.Title ?? '').trim(),
    }))
    .filter((t) => t.title);

  return {
    file: csvToFile(content, filename ?? 'classes.csv'),
    rows: transformedRows,
    titles,
    endDateWarnings: [],
  };
}

export async function rehydrateFamilyResult(
  jobId: string,
  platform: SourcePlatform,
  duplicatePolicies: FamilyWizardResult['duplicatePolicies'],
  filename?: string | null,
): Promise<FamilyWizardResult | null> {
  const content = await downloadImportDraftCsv(jobId, 'parents');
  const rows = parse<ParentRegistrationData>(content, {
    header: true,
    skipEmptyLines: true,
  }).data;
  const transformedRows = applyPlatformFamilyRowTransforms(rows, platform);

  return {
    file: csvToFile(content, filename ?? 'families-import.csv'),
    rows: transformedRows,
    familyCount: countParentGroupsFromCsvRows(transformedRows),
    duplicatePolicies,
  };
}

export function photoMappingsFromStaged(
  stagedImages: ImportJobStagedImage[],
): ClassPhotoMapping[] {
  return stagedImages.map((img) => ({
    rowIndex: img.rowIndex,
    classTitle: img.classTitle,
    staged: img,
  }));
}

export function resolveResumeStep(job: ImportJobResponse): number {
  if (
    job.status === 'queued' ||
    job.status === 'processing' ||
    job.status === 'completed' ||
    job.status === 'failed' ||
    job.status === 'cancelled'
  ) {
    return 5;
  }
  return Math.max(1, job.wizardStepReached ?? 0);
}

export function isSourcePlatformFromJob(
  value: string | undefined,
): value is SourcePlatform {
  return (
    typeof value === 'string' &&
    ['jackrabbit', 'studio_pro', 'studio_director', 'dsp', 'csv'].includes(value)
  );
}

export async function hydrateImportDraft(jobId: string): Promise<{
  job: ImportJobResponse;
  classResult: ClassWizardResult | null;
  familyResult: FamilyWizardResult | null;
  photoMappings: ClassPhotoMapping[];
  resumeStep: number;
  sourcePlatform: SourcePlatform;
} | null> {
  const job = await getImportJobStatus(jobId);
  if (job.sessionClosedAt) return null;
  // Cancelled jobs are not editable mid-wizard; clear stale session resume.
  if (job.status === 'cancelled') return null;

  // Unknown legacy platforms (dancebiz, fresh, other, …) must not become
  // Jackrabbit — that would re-apply JR-specific transforms. Use Upload CSV
  // (no auto-map transforms) as the safe fallback.
  const platform = isSourcePlatformFromJob(job.sourcePlatform)
    ? job.sourcePlatform
    : 'csv';

  const classResult = job.hasClassCsv
    ? await rehydrateClassResult(jobId, platform, job.classCsvFilename)
    : null;

  const familyResult = job.hasParentCsv
    ? await rehydrateFamilyResult(
        jobId,
        platform,
        job.duplicatePolicies ?? {},
        job.parentCsvFilename,
      )
    : null;

  return {
    job,
    classResult,
    familyResult,
    photoMappings: photoMappingsFromStaged(job.stagedImages ?? []),
    resumeStep: resolveResumeStep(job),
    sourcePlatform: platform,
  };
}

export function clearWizardSessionStorage() {
  localStorage.removeItem('enrollio-import-wizard-session');
}
